const cleanupStarted = new WeakSet()

// A shown/hidden listener can start the next phase (a `shown` handler that calls
// hide()), so phases are drained in a loop; a show → hide round trip is at most
// four of them.
const MAX_PHASES = 6

/**
 * Disposes a Bootstrap Modal without breaking a show/hide still in flight.
 *
 * Bootstrap 5.3 queues each phase's completion on a transitionend (with a timer
 * fallback), dispose() does not cancel it, and the queued callback then reads
 * fields dispose() has nulled — `this._config.focus` in the show's
 * `transitionComplete` ("Cannot read properties of null (reading 'focus')"),
 * `this._element.style` in the hide's. Waiting the phase out instead is no
 * better: the first phase of a show re-appends a modal that has meanwhile been
 * removed from the page to whatever <body> is current, where nothing owns it.
 *
 * So, as disposeTooltip does for tooltips, finish every phase now by firing the
 * transitionend Bootstrap would synthesize, then dispose:
 *
 * - show: `_showElement` is switched off first, so a show still in its backdrop
 *   fade never appends or reveals the modal; one already in its dialog
 *   transition completes (`shown.bs.modal`). A show that never got past the
 *   backdrop releases the body state it set (modal-open, scrollbar padding).
 * - hide: its dialog transition, then its backdrop fade, complete
 *   (`hidden.bs.modal`), so owners' hidden handlers clean up as usual.
 * - the backdrop is flushed on every pass, which also completes a hide whose
 *   fade a new show() overlapped.
 *
 * Not covered: the "static" backdrop bounce (`backdrop: "static"` or
 * `keyboard: false`), which queues on the dialog outside these phases. No modal
 * in the app uses either; handle `.modal-static` here before one does.
 *
 * The private fields read here are those of the installed Bootstrap 5.3.8; the
 * spec runs against it, so a rename turns it red.
 *
 * @param {import("bootstrap").Modal | null | undefined} modal
 */
export function disposeModal(modal) {
  // dispose() leaves `_element` null; a second dispose would throw.
  if (!modal || cleanupStarted.has(modal) || modal._element === null) return
  cleanupStarted.add(modal)

  for (let pass = 0; pass < MAX_PHASES && drainPhase(modal); pass++);
  modal.dispose()
}

// Completes the phase in flight, if any; returns whether another may follow.
function drainPhase(modal) {
  const backdrop = modal._backdrop?._isAppended ? modal._backdrop._element : null

  if (modal._isShown && modal._isTransitioning) {
    disableShowElement(modal)
    fireTransitionEnd(backdrop)
    fireTransitionEnd(modal._dialog)
    if (modal._isShown && modal._isTransitioning) {
      // Still mid-show: it never got past the backdrop, so nothing will finish it.
      releaseBody(modal)
      return false
    }
    return true
  }
  if (modal._isTransitioning) {
    fireTransitionEnd(modal._element)
    return true
  }
  if (!modal._isShown && backdrop) {
    fireTransitionEnd(backdrop)
    return true
  }
  return false
}

// dispose() assigns null to every own field; an accessor that ignores the write
// keeps a queued `this._showElement(...)` a no-op after it. Harmless once the
// method has already run.
function disableShowElement(modal) {
  const noop = () => {}
  Object.defineProperty(modal, "_showElement", { get: () => noop, set: () => {}, configurable: true })
}

function releaseBody(modal) {
  modal._scrollBar?.reset()
  if (!document.querySelector(".modal.show")) document.body.classList.remove("modal-open")
}

function fireTransitionEnd(element) {
  element?.dispatchEvent(new Event("transitionend"))
}
